// Editor state for the admin SSR heater panel.
//
// Mode is the single source of truth for what is editable. There is no
// separate per-row "is the form busy" flag that could drift out of step with
// it — TC-216 was caused by exactly that kind of duplicated state.
export const MODES = {
  VIEW: 'view',
  EDIT: 'edit',
  ADD: 'add',
};

// An SSR with no heaters arrives as [{}], not [], so filter on partNumber
// rather than on array length.
const lockedRowsFromSpecs = (specs) =>
  (Array.isArray(specs) ? specs : [])
    .filter((spec) => spec?.partNumber)
    .map((spec) => ({ partNumber: spec.partNumber, locked: true }));

export const initialEditorState = (specs) => ({
  mode: MODES.VIEW,
  rows: lockedRowsFromSpecs(specs),
});

export const heaterEditorReducer = (state, action) => {
  switch (action.type) {
    case 'enterEdit':
      return {
        mode: MODES.EDIT,
        rows: lockedRowsFromSpecs(action.specs).map((row) => ({
          ...row,
          locked: false,
        })),
      };

    case 'enterAdd':
      return {
        mode: MODES.ADD,
        rows: [
          ...lockedRowsFromSpecs(action.specs),
          { partNumber: '', locked: false },
        ],
      };

    case 'setPartNumber': {
      if (state.mode === MODES.VIEW) {
        return state;
      }
      const target = state.rows[action.index];
      if (!target || target.locked) {
        return state;
      }
      // Enter with no suggestion highlighted passes filteredSuggestions[-1],
      // which is undefined. Ignore it rather than coercing to '': an empty
      // box reads as "the admin removed this heater" and queues a delete.
      if (typeof action.partNumber !== 'string') {
        return state;
      }
      return {
        ...state,
        rows: state.rows.map((row, index) =>
          index === action.index
            ? { ...row, partNumber: action.partNumber.toUpperCase() }
            : row
        ),
      };
    }

    case 'removeRow': {
      if (state.mode === MODES.VIEW) {
        return state;
      }
      const target = state.rows[action.index];
      if (!target || target.locked) {
        return state;
      }
      return {
        ...state,
        rows: state.rows.filter((_, index) => index !== action.index),
      };
    }

    case 'cancel':
      return initialEditorState(action.specs);

    case 'syncFromSpecs':
      // Only while idle: reseeding mid-edit would discard the admin's work.
      return state.mode === MODES.VIEW
        ? initialEditorState(action.specs)
        : state;

    default:
      return state;
  }
};
